import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Modal, Spinner, Table } from 'react-bootstrap';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import dashboardAPI from '../../services/dashboard';
import PaginationComponent from '../PaginationComponent';

/**
 * #182201 — sếp: "nhấn vào dòng chữ đó thì hiển thị danh sách nhập hàng đang chờ, kiểu như mua
 * hàng Quang Nhung hay Shopee nhưng hàng chưa đến, NV chưa xác nhận nhập kho" — và chốt khuôn:
 * "giống như dòng chi phí NVL, nhấp vào là hiển thị bảng phiếu chi".
 *
 * Nên modal này bám ĐÚNG khuôn `ExpenseVouchersModal` (#235631): cùng cỡ `xl centered scrollable`,
 * cùng khối tóm tắt trên đầu, cùng cặp bảng-máy-tính / thẻ-điện-thoại, cùng `PaginationComponent`.
 * Không tự chế kiểu mới (luật #731598018/019).
 *
 * Dữ liệu: `GET /dashboard/pending-receipts` — CHÍNH endpoint mà dòng ngoài Dashboard dùng để lấy
 * số, nên số trên dòng và số dòng trong bảng KHÔNG THỂ lệch nhau.
 */

const LIMIT = 20;

const formatCurrency = (value) =>
  new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  try { return format(new Date(value), 'dd/MM/yyyy'); } catch { return value; }
};

const AMOUNT_STYLE = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

/**
 * #230341 — sếp: *"sao cứ để xác nhận nhập kho hoài như thế này? k có cơ chế gì để ràng buộc hay
 * xử lý hay sao?"* rồi chốt hướng bằng nút `nk_24h_leo`: hạn 24h, quá hạn thì nhắc NV, vẫn im thì
 * leo quản lý chi nhánh + hiện cảnh báo đỏ ngay trên màn này.
 *
 * Ngưỡng ĐỎ do server quyết (`is_overdue` trong `pending-receipts.dto.ts`), FE chỉ vẽ lại. Nếu FE
 * tự so giờ thì lệch múi giờ máy người xem là ra hai màn hình khác nhau cho cùng một đơn — và số
 * đỏ trên màn sẽ lệch với ngưỡng mà cron dùng để bắn tin.
 */
const formatPending = (hours) => {
  const h = Math.max(0, Math.floor(Number(hours) || 0));
  if (h < 48) return `${h} giờ`;
  return `${Math.floor(h / 24)} ngày`;
};

// Hai bước sếp cần phân biệt để biết phải giục ai: giục NCC hay giục nhân viên.
const STAGE_META = {
  NOT_ARRIVED: { label: 'Hàng chưa về', variant: 'warning', hint: 'Đã đặt NCC, chưa có phiếu kiểm hàng' },
  AWAITING_CONFIRM: { label: 'Chờ NV xác nhận nhập kho', variant: 'info', hint: 'Hàng đã về, NV đang kiểm / chưa xác nhận' },
};

const suppliersText = (names) => (names && names.length ? names.join(', ') : '—');

const PendingReceiptsModal = ({ show, onHide, branchId, branchName }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dashboardAPI.getPendingReceipts({
        page,
        limit: LIMIT,
        ...(branchId ? { branch_id: branchId } : {}),
      });
      setResult(res);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Không tải được danh sách đơn đang chờ nhập kho');
    } finally {
      setLoading(false);
    }
  }, [page, branchId]);

  useEffect(() => {
    if (!show) return;
    fetchData();
  }, [show, fetchData]);

  // Mở lại modal thì luôn về trang 1 để không thấy trang cũ của lần xem trước.
  useEffect(() => { if (show) setPage(1); }, [show, branchId]);

  const rows = result?.data || [];
  const total = result?.counts?.total ?? 0;
  const notArrived = result?.counts?.not_arrived ?? 0;
  const awaiting = result?.counts?.awaiting_confirm ?? 0;
  const overdue = result?.counts?.overdue ?? 0;
  // Đơn tệ nhất — server đã sắp phiếu kiểm mở lâu nhất lên đầu, nên lấy dòng quá hạn đầu tiên là
  // đúng đơn treo lâu nhất của trang 1. Cố ý chỉ đọc trang hiện tại: đây là câu tóm tắt cho cái
  // đang nhìn thấy, không phải một truy vấn thứ hai.
  const worst = (result?.data || []).find((po) => po.is_overdue) || null;

  // Bấm 1 dòng → mở thẳng đơn đặt hàng đó (khuôn "mọi chỗ dẫn đi phải dẫn tới đúng thứ đang xem").
  const openOrder = (po) => {
    onHide?.();
    navigate(`/inventory/purchase-order/${po.id}`);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="h5 mb-0">Đơn hàng đang chờ nhập kho</Modal.Title>
          <div className="small text-muted">
            {branchName || 'Tất cả chi nhánh'} · đơn đã đặt nhưng chưa hoàn tất nhập kho (không cắt theo khoảng ngày)
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" size="sm" /> Đang tải danh sách đơn đang chờ...
          </div>
        ) : error ? (
          <Alert variant="danger" className="mb-0">
            {error}
            <div className="mt-2">
              <Button size="sm" variant="outline-danger" onClick={fetchData}>Thử lại</Button>
            </div>
          </Alert>
        ) : (
          <>
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3 p-3 bg-light rounded">
              <div>
                <div className="text-muted small">Tổng cộng</div>
                <div className="h4 mb-0" style={AMOUNT_STYLE}>{total} đơn</div>
              </div>
              <div className="small">
                <Badge bg={STAGE_META.NOT_ARRIVED.variant} className="me-2">
                  {STAGE_META.NOT_ARRIVED.label}: {notArrived}
                </Badge>
                <Badge bg={STAGE_META.AWAITING_CONFIRM.variant}>
                  {STAGE_META.AWAITING_CONFIRM.label}: {awaiting}
                </Badge>
                {overdue > 0 && (
                  <Badge bg="danger" className="ms-2">Quá hạn 24h: {overdue}</Badge>
                )}
              </div>
            </div>

            {/* Sếp phải thấy ngay đơn nào tệ nhất mà không cần đọc hết bảng. */}
            {overdue > 0 && (
              <Alert variant="danger" className="py-2 px-3 mb-3">
                <div className="fw-semibold">
                  🚨 {overdue} đơn quá hạn 24h chưa được xác nhận nhập kho
                </div>
                {worst && (
                  <div className="small">
                    Tệ nhất: <strong>{worst.order_number}</strong> — treo{' '}
                    <strong>{formatPending(worst.pending_hours)}</strong>
                    {worst.checker_name ? ` · NV: ${worst.checker_name}` : ''}
                    {` · ${worst.branch_name}`}
                  </div>
                )}
                <div className="small text-muted">
                  Hàng đã về nhưng phiếu kiểm chưa chốt ⇒ tồn kho chưa được cộng. Hệ thống tự nhắc NV
                  khi chạm 24h và báo quản lý chi nhánh nếu vẫn chưa xác nhận.
                </div>
              </Alert>
            )}

            {total === 0 ? (
              <div className="text-center text-muted py-5">
                Không có đơn nào đang chờ nhập kho ở chi nhánh đang chọn.
              </div>
            ) : (
              <>
                {/* Máy tính: bảng */}
                <div className="d-none d-md-block table-responsive">
                  <Table hover size="sm" className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Mã đơn</th>
                        <th>Nhà cung cấp</th>
                        <th>Chi nhánh</th>
                        <th>Ngày đặt</th>
                        <th className="text-end">Tổng tiền</th>
                        <th>Đang ở bước nào</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((po) => {
                        const meta = STAGE_META[po.stage] || {};
                        return (
                          <tr
                            key={po.id}
                            className={po.is_overdue ? 'table-danger' : undefined}
                            style={{ cursor: 'pointer' }}
                            onClick={() => openOrder(po)}
                            title="Bấm để mở đơn đặt hàng"
                          >
                            <td className="fw-semibold text-primary text-nowrap">{po.order_number}</td>
                            <td className="small">{suppliersText(po.supplier_names)}</td>
                            <td className="small">{po.branch_name}</td>
                            <td className="text-nowrap small">
                              {formatDate(po.created_at)}
                              {po.expected_delivery_date && (
                                <div className="text-muted">Hẹn giao: {formatDate(po.expected_delivery_date)}</div>
                              )}
                            </td>
                            <td className="text-end fw-bold" style={AMOUNT_STYLE}>{formatCurrency(po.total_amount)}đ</td>
                            <td>
                              <Badge bg={po.is_overdue ? 'danger' : meta.variant || 'secondary'}>
                                {meta.label || po.stage}
                              </Badge>
                              {po.stage === 'AWAITING_CONFIRM' && (
                                <span
                                  className={`small ms-2 ${po.is_overdue ? 'text-danger fw-bold' : 'text-muted'}`}
                                >
                                  {po.is_overdue ? '🚨 ' : ''}treo {formatPending(po.pending_hours)}
                                </span>
                              )}
                              <div className="small text-muted">
                                {po.stage === 'AWAITING_CONFIRM'
                                  ? <>Phiếu kiểm {po.goods_receipt_number}{po.checker_name ? ` · NV: ${po.checker_name}` : ''}</>
                                  : meta.hint}
                              </div>
                              {/* Đã nhắc chưa — để quản lý không gọi lại người máy vừa nhắc, và
                                  thấy ngay đơn nào máy KHÔNG nhắc được (NV chưa khai Telegram). */}
                              {po.is_overdue && (
                                <div className="small text-danger">
                                  {po.manager_escalated_at
                                    ? 'Đã báo quản lý chi nhánh'
                                    : po.staff_reminder_sent_at
                                      ? 'Đã nhắc NV'
                                      : 'Chưa nhắc được — chờ lượt quét kế tiếp'}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                {/* Điện thoại: thẻ */}
                <div className="d-md-none">
                  {rows.map((po) => {
                    const meta = STAGE_META[po.stage] || {};
                    return (
                      <div
                        key={po.id}
                        className={`border-bottom py-2${po.is_overdue ? ' bg-danger-subtle border-danger' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => openOrder(po)}
                      >
                        <div className="d-flex justify-content-between gap-2">
                          <span className="fw-semibold text-primary">{po.order_number}</span>
                          <span className="fw-bold" style={AMOUNT_STYLE}>{formatCurrency(po.total_amount)}đ</span>
                        </div>
                        <div className="small">{suppliersText(po.supplier_names)}</div>
                        <div className="small text-muted">
                          {formatDate(po.created_at)} · {po.branch_name}
                          {po.expected_delivery_date && <> · hẹn giao {formatDate(po.expected_delivery_date)}</>}
                        </div>
                        <div className="mt-1">
                          <Badge bg={po.is_overdue ? 'danger' : meta.variant || 'secondary'}>
                            {meta.label || po.stage}
                          </Badge>
                          {po.stage === 'AWAITING_CONFIRM' && (
                            <span
                              className={`small ms-2 ${po.is_overdue ? 'text-danger fw-bold' : 'text-muted'}`}
                            >
                              {po.is_overdue ? '🚨 ' : ''}treo {formatPending(po.pending_hours)}
                            </span>
                          )}
                          {po.stage === 'AWAITING_CONFIRM' && po.checker_name && (
                            <span className="small text-muted ms-2">NV: {po.checker_name}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {total > LIMIT && (
                  <div className="mt-3">
                    <PaginationComponent
                      totalItems={total}
                      itemsPerPage={LIMIT}
                      currentPage={page}
                      onPageChange={setPage}
                      loading={loading}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Đóng</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PendingReceiptsModal;
